import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import Popover from '../base/Popover';
import OptionTile, { OptionTileGroup } from '../base/OptionTile';
import DomainIcon from '../base/icons/domain/DomainIcon';
import { useListNavigation } from '../../hooks/useListNavigation';
import { findSearchType, getSearchTypeOptions, type SearchTypeOption } from '../../config/propertyTypeCatalog';
import type { SearchCategory } from '../../config/searchFilterFields';
import { cx, useMediaQuery, useNsText } from './searchUi';

/**
 * The property-type picker: a grid of tiles, each with its domain icon, scoped to
 * the سكني / تجاري category. See Apps/web/THEME.md.
 *
 * - Options: config/propertyTypeCatalog `getSearchTypeOptions(category)`, the single
 *   source both search surfaces use. Labels: the i18n
 *   `search.filters.propertyTypeOptions.<ENUM>` dictionary, shared with /search.
 * - «كل الأنواع» comes first ('' = no type).
 * - `variant="popover"`: a select-style trigger whose grid opens BELOW it (Popover),
 *   3 columns from the `sm` breakpoint, 2 below. Focus stays on the trigger
 *   (`aria-activedescendant`), like SelectMenu: arrows move through the grid (Left /
 *   Right mirrored in RTL, Up / Down by row), Enter / Space pick, Escape closes, Tab
 *   picks the highlighted type and moves on, typing a letter jumps (Arabic-aware).
 * - `variant="inline"`: the same tiles in the page (the /search sidebar), a listbox
 *   with one Tab stop; long lists fold behind «عرض المزيد».
 *
 * `onChange` receives the UPPERCASE enum ('' for all types) plus the catalog entry,
 * whose `searchCategory` lets a caller keep the سكني / تجاري toggle in step.
 */

export interface PropertyTypePickerProps {
  /** UPPERCASE enum ('VILLA'), lowercase id ('villa') or '' for all types. */
  value: string;
  onChange: (value: string, option: SearchTypeOption | null) => void;
  /** The active سكني / تجاري toggle; '' or 'all' lists every type. */
  category: SearchCategory;
  variant?: 'popover' | 'inline';
  /** Accessible name. Default «نوع العقار». */
  label?: string;
  /** Default: hidden for a 'bare' trigger, visible otherwise. */
  hideLabel?: boolean;
  /** Id of the focusable trigger (popover) or of the tile list (inline). */
  id?: string;
  /** Popover: called each time the grid opens. */
  onOpen?: () => void;
  /** Popover: controlled open state (e.g. a «اختر نوع العقار» hint elsewhere opens it). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Popover trigger look. 'field' (default) is bordered; 'bare' fills a cell of the hero bar. */
  triggerVariant?: 'field' | 'bare';
  /** 'bare' trigger only: the label small inside the cell, above the value (the hero's tall fields). */
  inlineLabel?: boolean;
  /** Inline: grid columns. Default 2. */
  columns?: 2 | 3;
  /** Inline: tile layout. Default 'stack' (icon above the label), which suits narrow columns. */
  tileLayout?: 'row' | 'stack';
  /** Inline: types shown before «عرض المزيد». Default 5; 0 shows all. */
  initialCount?: number;
  /** Offer «كل الأنواع». Default true. */
  allowAny?: boolean;
  disabled?: boolean;
  className?: string;
}

interface TypeItem {
  /** UPPERCASE enum, '' for «كل الأنواع». */
  key: string;
  label: string;
  /** Rendered inside a tile, which sizes it. */
  icon: ReactNode | string;
  option: SearchTypeOption | null;
}

const ANY_ICON = 'ri-apps-2-line';

/** "rest_house" → "Rest house": only seen when the i18n dictionary is missing. */
function humanize(id: string): string {
  const words = id.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function optionDomId(listId: string, index: number): string {
  return `${listId}-option-${index}`;
}

export default function PropertyTypePicker({
  value,
  onChange,
  category,
  variant = 'popover',
  label,
  hideLabel,
  id,
  onOpen,
  open: openProp,
  onOpenChange,
  triggerVariant = 'field',
  inlineLabel = false,
  columns = 2,
  tileLayout = 'stack',
  initialCount = 5,
  allowAny = true,
  disabled = false,
  className = '',
}: PropertyTypePickerProps) {
  const { text: ui } = useNsText('ui');
  const { text: searchText } = useNsText('search');
  const generatedId = useId();
  const baseId = id ?? `type-picker-${generatedId}`;
  const labelId = `${baseId}-label`;

  const resolvedLabel = label ?? ui('typePicker.label', 'Property type');
  const anyLabel = ui('typePicker.anyType', 'All types');

  const items = useMemo<TypeItem[]>(() => {
    const scope = category === 'residential' || category === 'commercial' ? category : '';
    const types = getSearchTypeOptions(scope).map<TypeItem>((option) => ({
      key: option.enum,
      label: searchText(`search.filters.propertyTypeOptions.${option.enum}`, humanize(option.id)),
      icon: <DomainIcon id={option.iconId} className="h-[1.375rem] w-[1.375rem]" />,
      option,
    }));
    return allowAny ? [{ key: '', label: anyLabel, icon: ANY_ICON, option: null }, ...types] : types;
  }, [allowAny, anyLabel, category, searchText]);

  // Accept either casing; a value outside the current category selects no tile.
  const valueOption = findSearchType(value);
  const valueKey = valueOption?.enum ?? (value ? value.toUpperCase() : '');
  const selectedIndex = items.findIndex((item) => item.key === valueKey);

  const choose = (item: TypeItem | undefined) => {
    if (!item) return;
    if (item.key !== valueKey) onChange(item.key, item.option);
  };

  if (variant === 'inline') {
    return (
      <InlineTypeGrid
        items={items}
        selectedIndex={selectedIndex}
        onPick={choose}
        label={resolvedLabel}
        hideLabel={hideLabel ?? false}
        labelId={labelId}
        listId={baseId}
        columns={columns}
        tileLayout={tileLayout}
        initialCount={initialCount}
        allowAny={allowAny}
        disabled={disabled}
        showMoreLabel={(count) => ui('typePicker.showMore', 'Show more ({{count}})', { count })}
        showLessLabel={ui('typePicker.showLess', 'Show less')}
        className={className}
      />
    );
  }

  return (
    <PopoverTypeGrid
      items={items}
      selectedIndex={selectedIndex}
      valueOption={valueOption ?? null}
      valueKey={valueKey}
      emptyLabel={allowAny ? anyLabel : resolvedLabel}
      onPick={choose}
      label={resolvedLabel}
      hideLabel={hideLabel ?? triggerVariant === 'bare'}
      triggerId={baseId}
      labelId={labelId}
      listId={`${baseId}-listbox`}
      triggerVariant={triggerVariant}
      inlineLabel={inlineLabel}
      open={openProp}
      onOpenChange={onOpenChange}
      onOpen={onOpen}
      disabled={disabled}
      typeLabel={(option) => searchText(`search.filters.propertyTypeOptions.${option.enum}`, humanize(option.id))}
      className={className}
    />
  );
}

// ── Popover variant ─────────────────────────────────────────────────────────

interface PopoverTypeGridProps {
  items: TypeItem[];
  selectedIndex: number;
  valueOption: SearchTypeOption | null;
  valueKey: string;
  /** Trigger text while no type is chosen. */
  emptyLabel: string;
  onPick: (item: TypeItem | undefined) => void;
  label: string;
  hideLabel: boolean;
  triggerId: string;
  labelId: string;
  listId: string;
  triggerVariant: 'field' | 'bare';
  inlineLabel: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onOpen?: () => void;
  disabled: boolean;
  typeLabel: (option: SearchTypeOption) => string;
  className: string;
}

function PopoverTypeGrid({
  items,
  selectedIndex,
  valueOption,
  valueKey,
  emptyLabel,
  onPick,
  label,
  hideLabel,
  triggerId,
  labelId,
  listId,
  triggerVariant,
  inlineLabel,
  open: openProp,
  onOpenChange,
  onOpen,
  disabled,
  typeLabel,
  className,
}: PopoverTypeGridProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [openState, setOpenState] = useState(false);
  const open = (openProp ?? openState) && !disabled;
  const [activeIndex, setActiveIndex] = useState(-1);
  const wide = useMediaQuery('(min-width: 640px)');
  const columns = wide ? 3 : 2;

  const setOpen = useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
      if (next) onOpen?.();
    },
    [onOpen, onOpenChange, openProp],
  );

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const pick = (index: number) => {
    onPick(items[index]);
    close();
  };

  const nav = useListNavigation({
    count: items.length,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
    orientation: 'grid',
    columns,
    getLabel: (i) => items[i]?.label ?? '',
    onSelect: pick,
    selectOnSpace: true,
  });

  // Opened from outside (controlled `open`): start on the chosen tile.
  const startIndex = selectedIndex >= 0 ? selectedIndex : 0;
  useEffect(() => {
    if (open) setActiveIndex((i) => (i >= 0 && i < items.length ? i : startIndex));
    else setActiveIndex(-1);
  }, [open, items.length, startIndex]);

  // Keep the keyboard-highlighted tile in view inside a scrolled panel.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    document.getElementById(optionDomId(listId, activeIndex))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, listId, open]);

  const openAt = (index: number) => {
    if (disabled) return;
    setActiveIndex(index);
    if (!open) setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || event.nativeEvent.isComposing) return;
    const { key } = event;
    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        openAt(startIndex);
      } else if (key === 'Home') {
        event.preventDefault();
        openAt(0);
      } else if (key === 'End') {
        event.preventDefault();
        openAt(items.length - 1);
      }
      return;
    }
    if (key === 'Tab') {
      // APG select-only combobox: Tab keeps the highlighted choice and moves on.
      if (activeIndex >= 0) pick(activeIndex);
      else close();
      return;
    }
    if (key === 'ArrowUp' && event.altKey) {
      event.preventDefault();
      if (activeIndex >= 0) pick(activeIndex);
      else close();
      return;
    }
    nav.handleKeyDown(event);
  };

  const chosenLabel = valueOption ? typeLabel(valueOption) : valueKey || emptyLabel;
  const chosenIcon = valueOption ? (
    <DomainIcon id={valueOption.iconId} className="h-5 w-5" />
  ) : (
    <i className={ANY_ICON} />
  );
  const isBare = triggerVariant === 'bare';
  const labelInside = isBare && inlineLabel;

  return (
    <div className={cx('relative min-w-0', isBare && 'h-full', className)}>
      <span
        id={labelId}
        className={
          labelInside
            ? // Over the value, after the icon (px-4 + 1.5rem icon + gap-3).
              'pointer-events-none absolute end-4 start-[3.25rem] top-[7px] z-[1] truncate text-[0.75rem] font-semibold leading-none text-[var(--pv-color-text)]'
            : hideLabel
              ? 'sr-only'
              : 'mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]'
        }
        onClick={() => triggerRef.current?.focus()}
      >
        {label}
      </span>
      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={labelId}
        aria-activedescendant={open && activeIndex >= 0 ? optionDomId(listId, activeIndex) : undefined}
        aria-disabled={disabled || undefined}
        onClick={() => {
          if (disabled) return;
          if (open) close();
          else openAt(startIndex);
        }}
        onKeyDown={onKeyDown}
        className={cx(
          'flex min-h-[2.75rem] w-full cursor-pointer select-none items-center gap-3 px-4 text-start text-base outline-none',
          labelInside && 'pt-[17px]',
          isBare
            ? 'h-full rounded-[inherit] bg-transparent focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004D40]/20 dark:focus-visible:ring-[#3FBBA2]/25'
            : cx(
                'rounded-xl border bg-[var(--pv-color-surface-strong)] transition-colors duration-200 dark:bg-[#08372F]',
                'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-accent-soft)] focus-visible:border-[#004D40] focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:border-[#3FBBA2] dark:focus-visible:ring-[#3FBBA2]/25',
                open && 'border-[#004D40] dark:border-[#3FBBA2]',
              ),
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <span
          className="shrink-0 text-[1.5rem] leading-none text-[var(--pv-color-primary)] [&_svg]:h-6 [&_svg]:w-6"
          aria-hidden="true"
        >
          {chosenIcon}
        </span>
        <span
          className={cx(
            'min-w-0 flex-1 truncate',
            valueKey ? 'text-[var(--pv-color-text)]' : 'text-[var(--pv-color-text-muted)]',
          )}
        >
          {chosenLabel}
        </span>
        <i
          className={cx(
            'ri-arrow-down-s-line shrink-0 text-[1.125rem] leading-none text-[var(--pv-color-text-muted)] transition-transform duration-200',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </div>

      <Popover
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        anchorRef={triggerRef}
        width="anchor"
        minWidth="36rem"
      >
        <div
          id={listId}
          role="listbox"
          aria-labelledby={labelId}
          className="grid gap-2 p-2"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {items.map((item, index) => (
            <OptionTile
              key={item.key || '__any'}
              id={optionDomId(listId, index)}
              role="option"
              selected={index === selectedIndex}
              onSelect={() => pick(index)}
              label={item.label}
              icon={item.icon}
              layout="row"
              size="md"
              tabIndex={-1}
              // The trigger keeps focus (aria-activedescendant), so a press must not move it.
              onMouseDown={(event) => event.preventDefault()}
              data-active={index === activeIndex ? '' : undefined}
              className={
                index === activeIndex
                  ? 'ring-2 ring-[#004D40]/40 dark:ring-[#3FBBA2]/50'
                  : undefined
              }
            />
          ))}
        </div>
      </Popover>
    </div>
  );
}

// ── Inline variant ──────────────────────────────────────────────────────────

interface InlineTypeGridProps {
  items: TypeItem[];
  selectedIndex: number;
  onPick: (item: TypeItem | undefined) => void;
  label: string;
  hideLabel: boolean;
  labelId: string;
  listId: string;
  columns: 2 | 3;
  tileLayout: 'row' | 'stack';
  initialCount: number;
  allowAny: boolean;
  disabled: boolean;
  showMoreLabel: (count: number) => string;
  showLessLabel: string;
  className: string;
}

function InlineTypeGrid({
  items,
  selectedIndex,
  onPick,
  label,
  hideLabel,
  labelId,
  listId,
  columns,
  tileLayout,
  initialCount,
  allowAny,
  disabled,
  showMoreLabel,
  showLessLabel,
  className,
}: InlineTypeGridProps) {
  const [expanded, setExpanded] = useState(false);

  // Folded: «كل الأنواع» + the first `initialCount` types, plus the chosen type
  // wherever it sits, so the current choice is always on screen.
  const cut = initialCount > 0 ? initialCount + (allowAny ? 1 : 0) : items.length;
  const foldable = cut < items.length;
  const visible = useMemo(() => {
    if (!foldable || expanded) return items.map((item, index) => ({ item, index }));
    const shown = items.slice(0, cut).map((item, index) => ({ item, index }));
    if (selectedIndex >= cut) shown.push({ item: items[selectedIndex], index: selectedIndex });
    return shown;
  }, [cut, expanded, foldable, items, selectedIndex]);
  const hiddenCount = items.length - visible.length;

  return (
    <div className={cx('min-w-0', className)}>
      <span
        id={labelId}
        className={hideLabel ? 'sr-only' : 'mb-2 block text-[0.875rem] font-medium text-[var(--pv-color-text)]'}
      >
        {label}
      </span>
      <OptionTileGroup
        id={listId}
        role="listbox"
        columns={columns}
        aria-labelledby={labelId}
        aria-disabled={disabled || undefined}
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {visible.map(({ item, index }) => (
          <OptionTile
            key={item.key || '__any'}
            role="option"
            selected={index === selectedIndex}
            onSelect={() => onPick(item)}
            label={item.label}
            icon={item.icon}
            layout={tileLayout}
            size="sm"
            disabled={disabled}
          />
        ))}
      </OptionTileGroup>
      {foldable && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={listId}
          disabled={disabled}
          className={cx(
            'mt-2 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-xl px-3',
            'text-[0.875rem] text-[var(--pv-color-primary)] transition-colors hover:bg-[var(--pv-color-surface-soft)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:ring-[#3FBBA2]/25',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <span>{expanded ? showLessLabel : showMoreLabel(hiddenCount)}</span>
          <i
            className={cx('ri-arrow-down-s-line text-[1rem] leading-none transition-transform duration-200', expanded && 'rotate-180')}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}
